import { useState, useRef, useEffect } from "react";
import { SLIPPAGE_PRESETS, SLIPPAGE_AUTO } from "~/lib/constants";

export function SlippageControl({
  value,
  onChange,
}: {
  value: number;
  onChange: (bps: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [customEditing, setCustomEditing] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isAuto = value === SLIPPAGE_AUTO;
  const isCustom = !isAuto && !SLIPPAGE_PRESETS.includes(value);

  useEffect(() => {
    if (customEditing) inputRef.current?.focus();
  }, [customEditing]);

  useEffect(() => {
    if (!expanded) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setExpanded(false);
        setCustomEditing(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [expanded]);

  const displayLabel = isAuto
    ? "Auto"
    : `${value / 100}%`;

  const pills = (
    <div className="flex items-center gap-1 rounded-full bg-[var(--color-bg-card-section)] p-0.5">
      <button
        onClick={() => { onChange(SLIPPAGE_AUTO); setCustomEditing(false); setExpanded(false); }}
        className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
          isAuto ? "bg-[var(--color-accent)] text-white" : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
        }`}
      >Auto</button>
      {SLIPPAGE_PRESETS.map((preset) => (
        <button
          key={preset}
          onClick={() => { onChange(preset); setCustomInput(""); setCustomEditing(false); setExpanded(false); }}
          className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
            value === preset && !isAuto ? "bg-[var(--color-accent)] text-white" : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          }`}
        >{preset / 100}%</button>
      ))}
      {customEditing ? (
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          placeholder="0.5"
          value={customInput}
          onChange={(e) => {
            const val = e.target.value;
            if (/^[0-9]*[.]?[0-9]*$/.test(val)) {
              setCustomInput(val);
              const num = parseFloat(val);
              if (!isNaN(num) && num > 0 && num <= 50) onChange(Math.round(num * 100));
            }
          }}
          onBlur={() => setCustomEditing(false)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") { setCustomEditing(false); if (isCustom) setExpanded(false); } }}
          className="w-[52px] rounded-full bg-[var(--color-accent)] py-1 px-2 text-center text-xs font-medium text-white placeholder:text-white/40 focus:outline-none"
        />
      ) : (
        <button
          onClick={() => { setCustomInput(isCustom ? (value / 100).toString() : ""); setCustomEditing(true); }}
          className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
            isCustom ? "bg-[var(--color-accent)] text-white" : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          }`}
        >{isCustom ? `${value / 100}%` : "Custom"}</button>
      )}
    </div>
  );

  return (
    <div ref={containerRef} className="grid [&>*]:col-start-1 [&>*]:row-start-1">
      {/* Invisible expanded to reserve size */}
      <div className="pointer-events-none invisible" aria-hidden="true">{pills}</div>

      {/* Actual visible layer */}
      <div className="flex items-center justify-end">
        {expanded ? pills : (
          <button
            onClick={() => setExpanded(true)}
            className="flex items-center gap-1.5 rounded-full bg-[var(--color-bg-card-section)] px-2.5 py-1 text-xs font-medium text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
          >
            <span className="text-[var(--color-text-tertiary)]">Slippage</span>
            <span>{displayLabel}</span>
            {value > 500 && <span className="text-[10px] text-[var(--color-warning)]">!</span>}
          </button>
        )}
      </div>
    </div>
  );
}
